/**
 * SharedDashboardPage — public, unauthenticated view of a shared dashboard
 * layout.  Opens at /shared/:token.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Trophy, Medal, Users, Buildings, ChartLine, CalendarCheck,
  Sparkle, ArrowUpRight, Rocket, Fire, Crown, CloudCheck, Megaphone,
  ArrowsClockwise, Target as TargetIcon, ShareNetwork, Warning, Lock,
} from '@phosphor-icons/react';
import { RoleHomeAPI } from '../../api/roleHomeApi';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtShort = (n) => {
  if (n == null || !isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Number.isInteger(n) ? String(n) : n.toFixed(0);
};

export default function SharedDashboardPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, d] = await Promise.all([
          RoleHomeAPI.getPublicShare(token),
          RoleHomeAPI.getPublicShareData(token).catch(() => null),
        ]);
        setMeta(m);
        setData(d);
      } catch (e) {
        setError(e.message || 'Share link not found');
      }
    })();
  }, [token]);

  if (error) {
    return (
      <FullscreenPanel>
        <Lock size={48} weight="fill" style={{ color: '#EF4444' }} className="mx-auto mb-4" />
        <div className="text-2xl font-black mb-1" style={{ color: '#F5F5F5' }}>Link unavailable</div>
        <div className="text-sm" style={{ color: '#A3A3A3' }}>{error}</div>
      </FullscreenPanel>
    );
  }

  if (!meta) {
    return (
      <FullscreenPanel>
        <div className="w-10 h-10 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" />
      </FullscreenPanel>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#F5F5F5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
      data-testid="shared-dashboard-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        {/* Header */}
        <div className="mb-6 flex items-start gap-3 flex-wrap">
          <span className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #10B981, #06B6D4)' }}>
            <ShareNetwork size={22} weight="fill" style={{ color: '#0B0B0F' }} />
          </span>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: '#10B981' }}>
              Connection Source · Shared Dashboard
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {meta.name}
            </h1>
            <div className="text-xs mt-1" style={{ color: '#A3A3A3' }}>
              Shared by <span className="font-black" style={{ color: '#F5F5F5' }}>{meta.owner_name}</span>
              {' · '}Live data · Read-only
            </div>
          </div>
        </div>

        {/* Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(meta.catalog || []).map((w) => (
            <div key={w.id} className={w.id === 'hero_stats' ? 'lg:col-span-2' : ''}>
              <PublicWidget w={w} data={data} />
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-[11px]" style={{ color: '#737373' }}>
          Powered by Connection Source · Secured by share token · Revoke anytime from your dashboard settings.
        </div>
      </div>
    </div>
  );
}

function FullscreenPanel({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8"
      style={{ background: '#0A0A0A' }}>
      <div className="rounded-3xl p-10 text-center max-w-md w-full"
        style={{ background: 'rgba(20,20,20,0.7)', border: '1px solid #1F1F1F' }}>
        {children}
      </div>
    </div>
  );
}

function Card({ children, title, icon, accent, testId }) {
  return (
    <div className="rounded-2xl p-5 h-full"
      style={{ background: 'rgba(20,20,20,0.7)', border: '1px solid #1F1F1F' }}
      data-testid={testId}>
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center"
            style={{ background: `${accent}22`, color: accent }}>{icon}</span>
          <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: accent }}>{title}</div>
        </div>
      )}
      {children}
    </div>
  );
}

function PublicWidget({ w, data }) {
  switch (w.id) {
    case 'hero_stats':
      return (
        <Card title="Team Snapshot" icon={<Crown size={14} weight="fill" />} accent="#F5D64D" testId={`public-widget-${w.id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Active reps" value={data?.active_reps ?? '—'} color="#F59E0B" icon={<Users size={14} weight="fill" />} />
            <Stat label="Active stores" value={data?.active_stores ?? '—'} color="#10B981" icon={<Buildings size={14} weight="fill" />} />
            <Stat label="Reports" value={data?.source_count ?? '—'} color="#A855F7" icon={<ChartLine size={14} weight="fill" />} />
            <Stat label="Top metric" value={data?.top_reps?.[0]?.metric || '—'} color="#3B82F6" icon={<TargetIcon size={14} weight="fill" />} compact />
          </div>
        </Card>
      );
    case 'top_reps':
    case 'top_stores': {
      const isReps = w.id === 'top_reps';
      const list = data?.[isReps ? 'top_reps' : 'top_stores'] || [];
      const color = isReps ? '#F59E0B' : '#10B981';
      return (
        <Card title={isReps ? 'Top 10 Reps' : 'Top 10 Stores'}
          icon={isReps ? <Users size={14} weight="fill" /> : <Buildings size={14} weight="fill" />}
          accent={color}
          testId={`public-widget-${w.id}`}>
          {list.length === 0 ? (
            <div className="text-xs" style={{ color: '#A3A3A3' }}>No data yet.</div>
          ) : (
            <div className="space-y-1.5">
              {list.map((r, i) => (
                <div key={r.name} className="flex items-center gap-3 py-1.5 px-2 rounded-lg"
                  style={{ background: i === 0 ? `${color}11` : 'transparent' }}>
                  <span className="w-6 text-center text-sm font-black tabular-nums"
                    style={{ color: i === 0 ? color : '#A3A3A3' }}>
                    {i === 0 ? <Crown size={14} weight="fill" style={{ color }} /> : (i + 1)}
                  </span>
                  <span className="flex-1 min-w-0 truncate font-bold text-sm">{r.name}</span>
                  <span className="font-mono text-sm font-black tabular-nums" style={{ color }}>
                    {fmtShort(r.value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      );
    }
    case 'top_mover':
      return <PublicTopMover />;
    default:
      return (
        <Card title={w.title} icon={<Sparkle size={14} weight="fill" />} accent="#A855F7"
          testId={`public-widget-${w.id}`}>
          <div className="text-xs" style={{ color: '#A3A3A3' }}>{w.description}</div>
          <div className="text-[10px] mt-2" style={{ color: '#737373' }}>
            This widget shows live detail only when opened from within the app.
          </div>
        </Card>
      );
  }
}

function Stat({ label, value, color, icon, compact }) {
  return (
    <div className="rounded-xl p-3"
      style={{ background: 'rgba(10,10,10,0.7)', border: '1px solid #1F1F1F' }}>
      <div className="flex items-center gap-2 mb-1">
        <span style={{ color }}>{icon}</span>
        <div className="text-[9px] uppercase tracking-wider font-black" style={{ color: '#A3A3A3' }}>{label}</div>
      </div>
      <div className={`${compact ? 'text-xs' : 'text-2xl'} font-black tabular-nums`} style={{ color }}>{value}</div>
    </div>
  );
}

function PublicTopMover() {
  // No public endpoint for top-mover — render a friendly placeholder
  return (
    <Card title="Today's Top Mover" icon={<Rocket size={14} weight="fill" />} accent="#10B981">
      <div className="text-xs" style={{ color: '#A3A3A3' }}>
        Rank-jump data is available only inside the app. Log in to see today's top mover.
      </div>
    </Card>
  );
}
